// Moving things of the upper floors that go where a person goes: the librarian's book cart and the clerks' basketballs.
// Each entry (art/upper.py `follow_fx`) carries the id, speed and path of the walker it follows, so these draw from the
// same pure function of time that life.ts uses to place the walker: the cart is always in front of the librarian, the
// ball always in a clerk's hands, in the air on its way to the rim, or bouncing back. Import this module for its side
// effect (the Library and the court's screens do).
import type { FxEntry, Waypoint } from './assets';
import { registerFx, type FxContext } from './fx';
import { hashStr, walkAt } from './life';

const C = {
  ol: '#160f1a', h0: '#2c130c', h1: '#4b2015', h2: '#6f311d', h3: '#954727', h4: '#bb683c', o3: '#e6c48a',
  b1: '#8e6522', b2: '#c59b3c', m0: '#857d73', m1: '#a89f92', p1: '#e7dbbf', p2: '#fffbf2',
  v2: '#8f1c28', g2: '#3b6449', u1: '#2d3f65', s0: '#5d7fa8',
};

interface FollowFx extends FxEntry {
  pid: string;
  speed: number;
  path: Waypoint[];
  rim?: [number, number];
  land?: [number, number];
}

function px(ctx: CanvasRenderingContext2D, x: number, y: number, c: string, w = 1, h = 1): void {
  ctx.fillStyle = c;
  ctx.fillRect(Math.round(x), Math.round(y), w, h);
}

/** The walker's path in floor pixels (the entry stores it relative to its own spot). */
function absPath(e: FollowFx): Waypoint[] {
  return e.path.map((p) => [e.x + p[0], e.y + p[1], p[2], p[3]] as Waypoint);
}

export interface Follow {
  x: number;
  y: number;
  hidden: boolean;
  moving: boolean;
  /** -1 heading (or last headed) west, 1 east. */
  dir: number;
  /** While paused: which waypoint, and how far through the pause [0, 1). */
  stop: number;
  k: number;
}

/**
 * Where the followed walker is at `now`, with what walkAt does not say: which way they last headed and how far through
 * a pause they are. Position and visibility come from walkAt itself, so the two can never disagree.
 */
export function follow(path: Waypoint[], speed: number, now: number, h: number): Follow {
  const w = walkAt(path, speed, now, h);
  const n = path.length;
  const base: Follow = { x: w.x, y: w.y, hidden: w.hidden, moving: w.moving, dir: 1, stop: -1, k: 0 };
  if (n < 2) return base;
  const v = Math.max(1, speed) / 1000;
  const legs = path.map((p, i) => {
    const q = path[(i + 1) % n];
    return { p, q, pause: Math.max(0, p[2] ?? 0), dur: Math.hypot(q[0] - p[0], q[1] - p[1]) / v };
  });
  const total = legs.reduce((s, l) => s + l.pause + l.dur, 0);
  let t = (((now + (h % 600011)) % total) + total) % total;
  for (let i = 0; i < n; i += 1) {
    const l = legs[i];
    const prev = legs[(i + n - 1) % n];
    if (t < l.pause) {
      const dx = prev.q[0] - prev.p[0];
      return { ...base, dir: dx < 0 ? -1 : 1, stop: i, k: l.pause > 0 ? t / l.pause : 0 };
    }
    t -= l.pause;
    if (t < l.dur) {
      const dx = l.q[0] - l.p[0];
      return { ...base, dir: dx < 0 ? -1 : dx > 0 ? 1 : (prev.q[0] - prev.p[0] < 0 ? -1 : 1) };
    }
    t -= l.dur;
  }
  return base;
}

function placeOf(e: FollowFx, c: FxContext): Follow | null {
  const path = absPath(e);
  if (!path.length) return null;
  if (!c.animate) {
    const p = path[0];
    return { x: p[0], y: p[1], hidden: p[3] === 'door', moving: false, dir: 1, stop: 0, k: 0.1 };
  }
  return follow(path, e.speed ?? 18, c.now, hashStr(e.pid));
}

// ------------------------------------------------------------------ the librarian's cart
function cart(ctx: CanvasRenderingContext2D, e: FxEntry, c: FxContext): void {
  const f = placeOf(e as FollowFx, c);
  if (!f || f.hidden) return;
  // pushed ahead of her; parked beside her, on the side she came from, while she shelves
  const x0 = Math.round(f.dir < 0 ? f.x - 25 : f.x + 7);
  const by = f.y;
  const wob = f.moving ? Math.floor(c.now / 160) % 2 : 0;
  px(ctx, x0 + 1, by - 3, 'rgba(22,15,26,0.28)', 18, 3);               // its shadow
  px(ctx, x0 - 1, by - 15, C.ol, 20, 13);
  px(ctx, x0, by - 14, C.h2, 18, 11);
  px(ctx, x0, by - 14, C.h3, 18, 1);
  px(ctx, x0, by - 8, C.h1, 18, 1);
  // two shelves of returns
  const spines = [C.v2, C.g2, C.u1, C.p1, C.b1, C.v2, C.s0, C.h4];
  const seed = hashStr((e as FollowFx).pid);
  for (let i = 0; i < 5; i += 1) {
    const col = spines[(seed + i) % spines.length];
    px(ctx, x0 + 2 + i * 3, by - 19 + ((seed >> i) & 1), col, 2, 5 - ((seed >> i) & 1));
  }
  px(ctx, x0 + 1, by - 20, C.ol, 1, 6); px(ctx, x0 + 17, by - 20, C.ol, 1, 6);
  for (let i = 0; i < 4; i += 1) px(ctx, x0 + 3 + i * 4, by - 7, spines[(seed + i + 3) % spines.length], 3, 4);
  // the handle on her side, and the wheels turning
  const hx = f.dir < 0 ? x0 + 18 : x0 - 2;
  px(ctx, hx, by - 22, C.ol, 2, 9); px(ctx, hx, by - 22, C.m1, 1, 8);
  px(ctx, x0 + 1, by - 3, C.ol, 3, 3); px(ctx, x0 + 14, by - 3, C.ol, 3, 3);
  px(ctx, x0 + 2 + wob, by - 2, C.m0); px(ctx, x0 + 15 + wob, by - 2, C.m0);
}

// ------------------------------------------------------------------ a basketball
function ballAt(ctx: CanvasRenderingContext2D, x: number, y: number, floorY: number): void {
  const X = Math.round(x); const Y = Math.round(y);
  // the shadow on the boards, darker the closer the ball is to them
  const lift = Math.max(0, floorY - Y);
  ctx.globalAlpha = Math.max(0.12, 0.4 - lift / 120);
  px(ctx, X - 2, Math.round(floorY), C.ol, 5, 1);
  ctx.globalAlpha = 1;
  px(ctx, X - 1, Y - 3, C.ol, 4, 1); px(ctx, X - 2, Y - 2, C.ol, 6, 4); px(ctx, X - 1, Y + 2, C.ol, 4, 1);
  px(ctx, X - 1, Y - 2, C.h4, 4, 4); px(ctx, X - 1, Y + 1, C.h3, 4, 1); px(ctx, X + 1, Y - 2, C.h3, 1, 4);
  px(ctx, X - 1, Y - 2, C.o3);
  px(ctx, X, Y - 2, C.h1, 1, 4);
}

/** Dribbling height over the boards: in the hand at the top, on the floor at the bottom. */
function bounce(now: number, period: number, h: number): number {
  return Math.abs(Math.sin((Math.PI * (now + (h % 997))) / period));
}

function ball(ctx: CanvasRenderingContext2D, e0: FxEntry, c: FxContext): void {
  const e = e0 as FollowFx;
  const f = placeOf(e, c);
  if (!f || f.hidden) return;
  const h = hashStr(e.pid);
  const side = f.dir < 0 ? -7 : 7;
  const hand = f.y - 15;
  if (!c.animate) { ballAt(ctx, f.x + side, hand, f.y); return; }
  const dribble = (period: number, sx = f.x + side) => ballAt(ctx, sx, f.y - 2 - bounce(c.now, period, h) * 13, f.y);
  if (f.moving || f.stop < 0) { dribble(330); return; }
  const k = f.k;
  if (!e.rim || !e.land) {
    // no basket in reach: dribble in place, crossing over from hand to hand
    const cross = Math.floor((c.now + h) / 1300) % 2 ? -1 : 1;
    dribble(380, f.x + cross * 7);
    return;
  }
  const rimX = e.x + e.rim[0]; const rimY = e.y + e.rim[1];
  const landX = e.x + e.land[0]; const landY = e.y + e.land[1];
  const relX = f.x + 2; const relY = f.y - 46;
  const lerp = (a: number, b: number, u: number) => a + (b - a) * u;
  if (k < 0.3) { dribble(420); return; }
  if (k < 0.38) {                                   // gather and rise
    const u = (k - 0.3) / 0.08;
    ballAt(ctx, lerp(f.x + side, relX, u), lerp(hand, relY, u), f.y);
    return;
  }
  if (k < 0.62) {                                   // the shot
    const u = (k - 0.38) / 0.24;
    const arc = 16 + Math.hypot(rimX - relX, rimY - relY) * 0.22;
    ballAt(ctx, lerp(relX, rimX, u), lerp(relY, rimY - 4, u) - Math.sin(Math.PI * u) * arc, lerp(f.y, landY, u));
    return;
  }
  if (k < 0.68) {                                   // through the net
    const u = (k - 0.62) / 0.06;
    ballAt(ctx, rimX, lerp(rimY - 4, rimY + 9, u), landY);
    for (let i = 0; i < 4; i += 1) px(ctx, rimX - 6 + i * 4 + (Math.floor(c.now / 70) % 2), rimY + 3 + (i % 2), C.p2, 1, 5);
    return;
  }
  if (k < 0.76) {                                   // down to the boards, and a hop
    const u = (k - 0.68) / 0.08;
    const y = u < 0.6 ? lerp(rimY + 9, landY - 2, u / 0.6) : landY - 2 - Math.sin(Math.PI * (u - 0.6) / 0.4) * 5;
    ballAt(ctx, landX, y, landY);
    return;
  }
  // bounced back out to the shooter
  const u = (k - 0.76) / 0.24;
  const floor = lerp(landY, f.y, u);
  const lift = Math.abs(Math.sin(2 * Math.PI * u)) * 9 * (1 - u) + 13 * u * u;
  ballAt(ctx, lerp(landX, f.x + side, u), floor - 2 - lift, floor);
}

registerFx('cart', cart, 'tall');
registerFx('ball', ball, 'air');
